// The local agents this tab knows of, for the PR page and Settings
// (docs/superpowers/specs/2026-10-23-relay-pr-connections-design.md): the
// pairings and their grants on subjects (api/relaySubject.ts), the
// connections waiting for the person's approval, and this tab's serving of
// the agents' queries.
//
// Everything here is the browser's: the pairings and grants are read from
// the user's relay database (api/relay.ts), never from Changeling's server.
//
// Serving: once started (App, while signed in) and while the user has at
// least one pairing, this tab answers queries (`serveQueries` with
// `answerQuery`), listens for grant requests and grant changes. Each of
// those attaches the tab to the relay worker, which then binds the user's
// endpoint; a user who never paired an agent gets none of it. A first
// pairing made here engages at once; one made in another tab is told on a
// BroadcastChannel (which carries nothing but the news), and checked again
// when this tab is looked at again (`visibilitychange`). Once serving,
// every grant change in any tab reaches it (`onGrantsChanged`). The worker decides which attached tab
// answers (relay-serve.ts): only while it holds the endpoint, follow-up
// pages to the tab with their snapshot.
//
// Approvals: every connection ends with the person confirming it here, by
// the agent's fingerprint and harness: a pairing link (`#relay=`,
// relayInvite.ts) or an already paired agent's grant request. Their scope
// is preset to what the person chose (`presetOf`, which follows the grants
// as they change): the request started in Changeling's (`req`) when it
// names this subject, else the agent's grant on it in force, else (a first
// grant) the subject's kind's preset. A link can be approved only once the
// pairings have been read (`pairingsRead`), so it never starts wider than a
// grant not yet read. A link or a request for a history by its locator
// (`/commits/<spec>`, where only the repository says where the ref ends) is
// resolved here first (`resolveLocator`, as the history's page resolves
// it, a read of GitHub that tells the agent nothing), and the person
// approves the key it resolves to; the agent's computer hears the key only
// with that approval (`pair`, or `resolve` then `grant`), and holds it to
// the locator's own reading (relaySubject.ts `resolvesTo`). One that
// resolves to nothing shows no approval but an entry of its own (`unfound`)
// saying why, with Decline: nothing is declined but by the person.
//
// Last seen: when this browser last heard from each pairing's agent
// (`seen`, written by the worker as it serves and on pairing): when the
// agent last acted or listened, not merely its plugin running. An agent not
// heard from for STALE_MS is `stale`: the PR page doesn't offer it to
// Connect my agent, and says so of one that still holds a grant.
import { untrack } from 'svelte';
import { MOCK } from '../mock/flag';
import { laterCursor, showFingerprint, isAllowedRelay, parseInvite, relay, WORKER_DOWN, type AnswerCall, type RelayPresence, type GrantRequest, type RelayCategory, type RelayConnection, type RelayGrant } from '../api/relay';
import { kindOf, REQUEST_TTL_MS } from '../api/relay-grants';
import { locatorText, parseLocator, presetFor, resolvesTo, sameSubject, subjectKey, type SubjectKey } from '../api/relaySubject';
import type { RefLocator } from '../subject';
import { resolveRef } from './ref/fetch';
import { lacking, type TaskBody } from '../api/relayTask';
import { INVITE_CAPTURED, takeInvite } from '../relayInvite';
import { routeHarness } from '../logic/relayHarness';
import { answerQuery, dropSnapshots } from '../relayTools';
import type { RelayQuery } from '../api/relay-grants';
import { syncResults, type SyncDeps } from './agentNotes';
import { acquireNotes } from './notes.svelte';

/** Where agents' results are kept: the repository's notes store (an open page of it shows them). */
const keepNote: SyncDeps['keep'] = async (owner, repo, note) => {
  const { store, release } = acquireNotes(owner, repo);
  try {
    return await store.keep(note);
  } finally {
    release();
  }
};

export type Invite = NonNullable<ReturnType<typeof parseInvite>>;
interface ApprovalBase {
  /** Distinguishes approvals: `pair:<connection>` or `grant:<connection>\0<request id>`. */
  key: string;
  /** What it grants: the subject the agent named, or the one its locator resolved to. */
  subject: SubjectKey;
  /** The pairing it is of, or (a link's) would be: `<peer>/<route>`. */
  connection: string;
  /** The agent's fingerprint, from its pinned endpoint ID. */
  fingerprint: string;
  /** The agent's name, when this browser knows it (once paired). */
  name: string | null;
  /** The harness it says it is (relayHarness.ts): the pairing's, as its computer said, or what a link's route says (a RouteHarness). */
  harness: string | null;
  /** The scope the person chose for it with a request started in Changeling (`req`) naming its subject, if any. */
  requested: RelayCategory[] | null;
}
/**
 * A paired agent's request for a history whose locator resolves to
 * nothing: no approval (there is no subject to grant), but an entry of its
 * own, kept until the person declines it or the request goes, so no
 * notice or second such request hides it (RelayNotices shows it with
 * Decline only).
 */
export interface Unfound {
  /** `grant:<connection>\0<request id>`, as the request's approval would be keyed. */
  key: string;
  connection: string;
  request: string;
  /** The agent's name, its locator's wire text, and why it resolves to nothing. */
  name: string;
  locator: string;
  why: string;
}
/** A grant request also keeps what the agent asked for (`asks`): a grant of just that settles it. */
export type Approval = (ApprovalBase & { kind: 'pair'; invite: Invite }) | (ApprovalBase & { kind: 'grant'; request: GrantRequest; asks: RelayCategory[] });

/** The key of the history `l` names, as its page resolves it (state/ref/fetch.ts `resolveRef`): a read of GitHub, behind what is on screen. */
function resolveHistory(l: RefLocator): Promise<SubjectKey> {
  return resolveRef(l, { priority: 'background', signal: new AbortController().signal }).then((r) => subjectKey(r.subject));
}
/** A line for the person: what an approval came to, or why a link was refused. */
export interface Notice {
  text: string;
  tone: 'status' | 'alert';
  subject?: SubjectKey;
  /** Kept until dismissed, like an alert (a warning to act on), though said politely. */
  persist?: boolean;
  /** The pairing it warns of (`persist`): dismissing it ends that pairing's Just connected mark. */
  connection?: string;
}
/** Where approvals and notices are shown inline: one subject's page, or every one (Settings). */
export type View = SubjectKey | 'all';

/** The channel on which a tab says it paired an agent, so tabs not serving yet read the pairings again. */
export const PAIRED_CHANNEL = 'changeling:relay-paired';


const why = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** What an answer whose time ran out says. */
export const TOOK_TOO_LONG = 'Reading this from GitHub took too long. Ask again.';
/** How long before its time runs out a paged answer goes with what it has read (relayTools' `hurry`). */
export const HURRY_MS = 1_500;
/** How long an approval's Connect waits, shown and unmoved, before it can be used (RelayApprovalCard). */
export const ARM_MS = 600;
/** How long a status notice stays (one saying what came of an approval); alerts stay until dismissed. */
export const NOTICE_MS = 10_000;
/** How long since an agent was last heard from before it counts as gone (`stale`). */
export const STALE_MS = 60 * 60 * 1000;
/** How often `now` moves on while started: last seen is shown to the minute. */
export const TICK_MS = 60_000;
/** Resolves after `ms` unless `signal` aborts first, when it never resolves. */
function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => clearTimeout(timer));
  });
}


/** Whether `after` lacks a category `before` had. */
const narrowed = (before: readonly RelayCategory[], after: readonly RelayCategory[]) => before.some((c) => !after.includes(c));

/** An agent's presence, as its computer said; `reachable` false when its computer couldn't be asked. */
export interface AgentPresence { presence: RelayPresence; reachable: boolean }

export class RelayAgents {
  connections: RelayConnection[] = $state.raw([]);
  /** When this browser last heard from each pairing's agent, by connection (none: not yet). */
  seen: Record<string, number> = $state.raw({});
  /** The time `stale` and last seen are counted from: `clock()`, moved on every TICK_MS while started. */
  now = $state(Date.now());
  /** The clock (tests replace it, then `tick`). */
  clock: () => number = Date.now;
  /**
   * Each agent's presence as its computer last said (`checkPresence`), or
   * `checking` while asked; none until first asked. The PR header's agent
   * button and the Local agents card read the one answer.
   */
  presence: Record<string, AgentPresence | 'checking'> = $state({});
  /** This browser's grants, granted or refused by the agent's computer (`status`). */
  grants: RelayGrant[] = $state.raw([]);
  approvals: Approval[] = $state.raw([]);
  /** Requests for histories that resolve to nothing, each until the person declines it or it goes. */
  unfound: Unfound[] = $state.raw([]);
  notice: Notice | null = $state.raw(null);
  /** Pairings made in this tab since it started, which the PR page says to revoke if they are not the agent asked for. */
  connected: string[] = $state.raw([]);
  /** Topics (review threads, a subject's conversation) sent from this tab: their threads open their Local agent panel (Thread). */
  sentTopics: string[] = $state.raw([]);
  /** Whether the pairings have been read once, or failed to be. */
  loaded = $state(false);
  /** Whether the pairings and grants have been read since `start` (not just tried: a failed read leaves this false). */
  pairingsRead = $state(false);
  /** Why they could not be read. */
  error = $state('');
  /** Why this tab's encrypted agent connection is down (relay.workerDown): it kept stopping, and starts again only when asked (`revive`). */
  workerDown: string | null = $state.raw(null);
  /** Whether a `revive` is on its way. */
  reviving = $state(false);
  /** The views showing approvals inline (`show`); the app's notices show the rest. */
  views: View[] = $state.raw([]);
  /** The subjects whose approvals and notices a panel of their own shows (`host`, the PR header's agent panel), not the app's dock. */
  hosts: SubjectKey[] = $state.raw([]);
  /** The delay before an approval's Connect is armed (tests replace it). */
  armDelay: (ms: number, signal: AbortSignal) => Promise<void> = wait;
  /** The delay before a status notice clears (tests replace it). */
  noticeDelay: (ms: number, signal: AbortSignal) => Promise<void> = wait;
  /** How a locator an agent asks for is resolved to its key (tests replace it). */
  resolveLocator: (l: RefLocator) => Promise<SubjectKey> = resolveHistory;

  /**
   * The key `l` resolves to, held to the locator's own reading (what the
   * agent's computer checks too), or why there is none.
   */
  private async located(l: RefLocator): Promise<{ key: SubjectKey } | { why: string }> {
    try {
      const key = await this.resolveLocator(l);
      return resolvesTo(l, key) ? { key } : { why: `${locatorText(l)} resolved to ${key}, which it does not name.` };
    } catch (e) {
      return { why: why(e) };
    }
  }

  /**
   * Clears `n`, a status notice, after NOTICE_MS unless another replaced it
   * or `held` (focus or the pointer on it) says to keep it; then it clears
   * once no longer held, when asked again. Returns the cancel.
   */
  expire(n: Notice, held: () => boolean): () => void {
    const ctl = new AbortController();
    if (n.tone !== 'status' || n.persist) return () => ctl.abort();
    void this.noticeDelay(NOTICE_MS, ctl.signal).then(() => {
      if (!ctl.signal.aborted && this.notice === n && !held()) this.notice = null;
    });
    return () => ctl.abort();
  }

  private started = false;
  private offs: (() => void)[] = [];
  private seq = 0;
  private answering = new Set<AbortController>();
  /** Grant requests settled or withdrawn, with when, so one whose preset was still being read isn't shown. */
  private withdrawn = new Map<string, number>();
  /** Bumped by `start` and `stop`: work begun under an earlier one (another account, maybe) is dropped. */
  private epoch = 0;
  private stopListening: (() => void) | null = null;
  private ticker: ReturnType<typeof setInterval> | undefined;
  private channel: BroadcastChannel | null = null;
  private running = new Set<Promise<unknown>>();
  /** Runs `p` as this store's own work, which `drained` waits for. */
  private track<T>(p: Promise<T>): Promise<T> {
    this.running.add(p);
    void p.finally(() => this.running.delete(p)).catch(() => {});
    return p;
  }
  /** Settles once the work this store started itself (reads, grant requests, links) has ended (tests). */
  async drained(): Promise<void> {
    while (this.running.size) await Promise.allSettled([...this.running]);
  }

  /** Starts taking pairing links, reading the pairings, and serving while there are some. Returns `stop`. */
  start(): () => void {
    if (MOCK || this.started) return () => {};
    this.started = true;
    this.epoch++;
    const onInvite = () => void this.track(this.receive());
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      this.tick();
      if (!this.offs.length) void this.track(this.refresh());
      else this.syncResults();
    };
    window.addEventListener(INVITE_CAPTURED, onInvite);
    document.addEventListener('visibilitychange', onVisible);
    const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(PAIRED_CHANNEL);
    if (channel) channel.onmessage = () => { if (!this.offs.length) void this.track(this.refresh()); };
    this.channel = channel;
    this.workerDown = relay.workerDown();
    this.tick();
    this.ticker = setInterval(() => this.tick(), TICK_MS);
    const offDown = relay.onWorkerDown((why) => (this.workerDown = why));
    // A serve's word that a pairing finished a range task: its results are kept now.
    const offResults = relay.onResults((id, after) => this.syncResults(id, after));
    this.stopListening = () => {
      window.removeEventListener(INVITE_CAPTURED, onInvite);
      document.removeEventListener('visibilitychange', onVisible);
      offDown();
      offResults();
      channel?.close();
      this.channel = null;
    };
    void this.track(this.receive());
    void this.track(this.refresh());
    return () => this.stop();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.seq++;
    this.epoch++;
    this.withdrawn.clear();
    this.stopListening?.();
    this.stopListening = null;
    clearInterval(this.ticker);
    this.ticker = undefined;
    this.disengage();
    this.connections = [];
    this.seen = {};
    this.presence = {};
    this.presenceAsked.clear();
    this.grants = [];
    this.approvals = [];
    this.unfound = [];
    this.notice = null;
    this.connected = [];
    this.sentTopics = [];
    this.loaded = false;
    this.pairingsRead = false;
    this.error = '';
    this.workerDown = null;
    this.reviving = false;
  }

  /**
   * Starts the encrypted agent connection again once it is down (the
   * person's Try again), then says what came of it: running again, or why
   * it didn't start (the down line stays while it is still down).
   */
  async revive(): Promise<void> {
    if (this.reviving) return;
    this.reviving = true;
    const epoch = this.epoch;
    try {
      await relay.revive();
      if (epoch === this.epoch) this.notice = { text: 'The encrypted agent connection is running again.', tone: 'status' };
    } catch (e) {
      if (epoch === this.epoch) this.notice = { text: `The encrypted agent connection didn’t come back. ${why(e)}`, tone: 'alert' };
    } finally {
      if (epoch === this.epoch) this.reviving = false;
    }
  }

  /** Shows approvals and notices for `view` inline while the returned function isn't called. */
  show(view: View): () => void {
    // Called from effects, which don't depend on the views this changes.
    untrack(() => (this.views = [...this.views, view]));
    return () => untrack(() => {
      const i = this.views.indexOf(view);
      if (i >= 0) this.views = this.views.filter((_, j) => j !== i);
      // Its page left: a status line about it is done with (a warning stays until dismissed).
      const n = this.notice;
      if (n?.subject && !n.persist && n.tone === 'status' && !this.shownInline(n.subject)) this.notice = null;
    });
  }
  /**
   * Says that `subject`'s approvals and notices are shown by a panel of its
   * own (the PR header's agent panel) while the returned function isn't
   * called: the dock leaves them out.
   */
  host(subject: SubjectKey): () => void {
    // Its own entry, so two panels of one subject leave one each.
    const entry = (subject + '') as SubjectKey;
    untrack(() => (this.hosts = [...this.hosts, entry]));
    return () => untrack(() => {
      const i = this.hosts.indexOf(entry);
      if (i >= 0) this.hosts = this.hosts.filter((_, j) => j !== i);
    });
  }
  /** Whether `subject`'s approvals and notices have a panel of their own (`host`). */
  hosted(subject?: SubjectKey): boolean {
    return !!subject && this.hosts.some((h) => sameSubject(h, subject));
  }
  /** Whether a view shows what concerns `subject` (or, with none, what concerns no subject). */
  shownInline(subject?: SubjectKey): boolean {
    return this.views.some((v) => v === 'all' || (!!subject && sameSubject(v, subject)));
  }

  /** The approvals `view` shows. */
  approvalsFor(view: View): Approval[] {
    return view === 'all' ? this.approvals : this.approvals.filter((a) => sameSubject(a.subject, view));
  }
  /** The grants on `subject`. */
  grantsOn(subject: SubjectKey): RelayGrant[] {
    return this.grants.filter((g) => sameSubject(g.subject, subject));
  }
  /** The agents a task on `subject` that carries `needs` (relayTask.ts) can be sent to: their grant on it in force, and covering those. */
  taskAgents(subject: SubjectKey, needs: readonly RelayCategory[]): RelayConnection[] {
    return this.grantsOn(subject).flatMap((g) => {
      const c = g.status === 'granted' && !lacking(needs, g.scope).length ? this.connection(g.connection) : undefined;
      return c ? [c] : [];
    });
  }
  /**
   * Sends a review task (`topic`, and `body`: RelayThread's threadTask, or
   * a question about the subject) to `agent`, and notes it sent from this
   * tab (`sentTopics`), so its thread opens its Local agent panel wherever
   * it was sent from (the composer's Save & send).
   */
  async sendTask(agent: string, topic: string, body: TaskBody): Promise<void> {
    // Noted before it is delivered: a delivery that fails may still have reached it (relay.send records it too).
    if (!this.sentTopics.includes(topic)) this.sentTopics = [...this.sentTopics, topic];
    await relay.send(agent, topic, body);
  }
  connection(id: string): RelayConnection | undefined {
    return this.connections.find((c) => c.id === id);
  }
  /** The pairings whose results are being read now (`syncResults`). */
  private syncing = new Set<string>();
  /** Whether `subject` is granted to `connection` now with `files`, what a range task carries. */
  private rangeGranted(connection: string, subject: SubjectKey): boolean {
    return this.grants.some((g) => g.connection === connection && g.status === 'granted' && sameSubject(g.subject, subject) && g.scope.includes('files'));
  }
  /**
   * Runs `fn` holding the browser-wide lock `name` if no tab holds it, else
   * not at all (`navigator.locks`, `ifAvailable`): so one tab of the
   * browser reads a pairing's results at a time. Tests replace it.
   */
  resultsLock: (name: string, fn: () => Promise<unknown>) => Promise<void> = async (name, fn) => {
    if (typeof navigator === 'undefined' || !navigator.locks) return void (await fn());
    await navigator.locks.request(name, { ifAvailable: true }, async (lock) => {
      if (lock) await fn();
    });
  };
  /**
   * Keep granted pairings' finished results as their agents' notes
   * (state/agentNotes.ts), whether or not a panel shows them. Pushed: when
   * a serve says pairing `only` finished a range task (`after`, its newest
   * result's cursor), and this browser's cursor is behind it. Caught up:
   * every granted pairing once the pairings are read and when the tab is
   * looked at again. One tab of the browser reads a pairing at a time
   * (`resultsLock`): another tab holding it reads what is new, and a
   * notice this one skips is said again at the next serve.
   */
  syncResults(only?: string, after?: string): void {
    if (!this.started || !this.pairingsRead || this.workerDown) return;
    for (const c of this.connections) {
      if ((only !== undefined && c.id !== only) || this.syncing.has(c.id) || !this.grants.some((g) => g.connection === c.id && g.status === 'granted')) continue;
      this.syncing.add(c.id);
      const deps: SyncDeps = {
        results: (id, after, limit) => relay.results(id, after, limit),
        cursor: (id) => relay.resultsCursor(id),
        advance: (id, after) => relay.advanceResults(id, after),
        sent: (id) => relay.sentRanges(id),
        granted: (id, subject) => this.rangeGranted(id, subject),
        keep: keepNote,
      };
      const read = async () => {
        if (after !== undefined && !laterCursor(after, await relay.resultsCursor(c.id))) return;
        await syncResults(c.id, c.name, deps);
      };
      void this.track(
        this.resultsLock(`relay-results:${c.id}`, read)
          .catch(() => {})
          .finally(() => this.syncing.delete(c.id)),
      );
    }
  }
  /** Moves `now` on to the clock. */
  tick(): void {
    this.now = this.clock();
  }
  /** When this browser last heard from `connection`'s agent, if it has. */
  lastSeen(connection: string): number | undefined {
    return this.seen[connection];
  }
  /** Whether `connection`'s agent hasn't been heard from for STALE_MS (or ever, here). */
  stale(connection: string): boolean {
    const at = this.seen[connection];
    return at === undefined || this.now - at > STALE_MS;
  }

  /** The presences being asked now, each with the `epoch` it was asked under. */
  private presenceAsked = new Map<string, number>();
  /**
   * Asks `connection`'s computer for its agent's presence, unless it is
   * being asked or (without `again`) was answered. Not while the agent
   * connection is down, when asking would only fail; one caught by it going
   * down is left `checking`, to be asked again.
   */
  checkPresence(connection: string, again = false): void {
    if (this.workerDown || this.presenceAsked.has(connection)) return;
    const was = this.presence[connection];
    if (!again && was !== undefined && was !== 'checking') return;
    if (was === undefined || was === 'checking') this.presence[connection] = 'checking';
    const epoch = this.epoch;
    this.presenceAsked.set(connection, epoch);
    void this.track(relay.status(connection).then(
      (s) => ({ presence: s.presence, reachable: true }),
      (e) => (e instanceof Error && e.message === WORKER_DOWN ? 'checking' as const : { presence: 'offline' as const, reachable: false }),
    ).then((p) => {
      if (epoch !== this.epoch || !this.connection(connection)) return;
      this.presence[connection] = p;
      // Answered, and the agent listening or wakeable: it is there now. One
      // only connected (its plugin running, the agent idle) may have sat
      // so for hours: the helper's last seen says when it last acted.
      if (p !== 'checking' && p.reachable && (p.presence === 'listening' || p.presence === 'wakeable' || p.presence === 'channel')) this.seen = { ...this.seen, [connection]: this.clock() };
    }).finally(() => { if (this.presenceAsked.get(connection) === epoch) this.presenceAsked.delete(connection); }));
  }

  /** Reads the pairings and grants again, and serves while there are pairings. */
  async refresh(): Promise<void> {
    if (!this.started) return;
    const n = ++this.seq;
    try {
      const { connections, seen } = await relay.list();
      // In a steady order, whatever order the changes were made in.
      const grants = (connections.length ? await relay.grants() : []).sort((x, y) => x.subject.toLowerCase().localeCompare(y.subject.toLowerCase(), 'en', { numeric: true }));
      if (n !== this.seq) return;
      this.forgetSnapshots(connections, grants);
      this.connections = connections;
      // What the worker said while this read was out may be later than what it read.
      this.seen = Object.fromEntries(connections.flatMap((c) => {
        const at = Math.max(seen[c.id] ?? -Infinity, this.seen[c.id] ?? -Infinity);
        return Number.isFinite(at) ? [[c.id, at]] : [];
      }));
      this.grants = grants;
      this.pairingsRead = true;
      this.error = '';
      // A request of a pairing gone can't be granted, and one a grant now covers is settled here.
      const ids = new Set(connections.map((c) => c.id));
      this.approvals = this.approvals.filter((a) => a.kind === 'pair' || (ids.has(a.request.connection) && !this.covered(a.request.connection, a.subject, a.asks)));
      if (connections.length) this.engage();
      else this.disengage();
      this.syncResults();
    } catch (e) {
      if (n === this.seq) this.error = why(e);
    } finally {
      if (n === this.seq) this.loaded = true;
    }
  }

  /**
   * Drops this tab's paging snapshots for what is no longer granted as it
   * was: a pairing gone, a grant revoked, narrowed or refused. Another
   * tab's change reaches here through `onGrantsChanged`.
   */
  private forgetSnapshots(connections: RelayConnection[], grants: RelayGrant[]) {
    const ids = new Set(connections.map((c) => c.id));
    for (const c of this.connections) if (!ids.has(c.id)) dropSnapshots(c.id);
    for (const g of this.grants) {
      if (!ids.has(g.connection)) continue;
      const now = grants.find((x) => x.connection === g.connection && sameSubject(x.subject, g.subject));
      if (!now || now.status === 'refused' || narrowed(g.scope, now.scope)) dropSnapshots(g.connection, g.subject);
    }
  }

  private engage() {
    if (this.offs.length) return;
    this.offs = [
      relay.onGrantRequest(
        (r) => void this.track(this.request(r)),
        (id, connection) => this.gone(id, connection),
      ),
      relay.onGrantsChanged(() => void this.track(this.refresh())),
      relay.onSeen((connection, at) => {
        if (this.connection(connection) && !((this.seen[connection] ?? -Infinity) >= at)) this.seen = { ...this.seen, [connection]: at };
      }),
      relay.serveQueries((q, call) => this.answer(q, call)),
    ];
  }
  private disengage() {
    for (const off of this.offs) off();
    this.offs = [];
    for (const a of this.answering) a.abort(new Error('This tab stopped serving.'));
    this.answering.clear();
    // Only a serving tab hears of grant requests settling.
    this.approvals = this.approvals.filter((a) => a.kind === 'pair');
  }

  /**
   * Answers an agent's query from this tab, within the grants as they are
   * when it reads and when it answers. Its reads stop once `ms` (what the
   * worker said is left before the answer is too late) has passed, or when
   * the worker passes the query on (`signal`), and a paged answer goes with
   * what it has read HURRY_MS before that. Each GraphQL page is first asked
   * of the worker's budget (`spend`).
   */
  async answer(q: RelayQuery, { ms, signal, spend }: AnswerCall): Promise<unknown> {
    const abort = new AbortController();
    this.answering.add(abort);
    // The worker passed it on to another tab.
    const passed = () => abort.abort(signal.reason);
    if (signal.aborted) passed(); else signal.addEventListener('abort', passed, { once: true });
    const timer = setTimeout(() => abort.abort(new Error(TOOK_TOO_LONG)), Math.max(0, ms));
    const hurry = new AbortController();
    const hurried = setTimeout(() => hurry.abort(), Math.max(0, ms - HURRY_MS));
    try {
      return await answerQuery(q, () => relay.grants(), { signal: abort.signal, now: Date.now, hurry: hurry.signal, spend });
    } finally {
      signal.removeEventListener('abort', passed);
      clearTimeout(timer);
      clearTimeout(hurried);
      this.answering.delete(abort);
    }
  }

  /** The scope a request `req` started in Changeling chose for `subject`, if it names it. */
  private async requested(req: string | undefined, subject: SubjectKey): Promise<RelayCategory[] | null> {
    if (!req) return null;
    try {
      const found = await relay.findRequest(req);
      return found && sameSubject(found.subject, subject) ? [...found.scope] : null;
    } catch {
      return null;
    }
  }
  /** `connection`'s grant on `subject` in force, if any. */
  current(connection: string, subject: SubjectKey): RelayGrant | undefined {
    return this.grants.find((g) => g.connection === connection && sameSubject(g.subject, subject) && g.status === 'granted');
  }
  /**
   * What an approval's boxes start with, as the grants are now: what the
   * person chose with a request started in Changeling, else the agent's
   * grant in force on the subject, else its kind's preset (relaySubject.ts
   * `presetFor`). Meaningful once `pairingsRead`.
   */
  presetOf(a: Approval): RelayCategory[] {
    return a.requested ?? [...(this.current(a.connection, a.subject)?.scope ?? presetFor(kindOf(a.subject)))];
  }

  /** Whether `connection` holds a grant in force on `subject` with just `scope`, which a request for it would only repeat. */
  private covered(connection: string, subject: SubjectKey, scope: readonly RelayCategory[]): boolean {
    return this.grants.some((g) => g.connection === connection && sameSubject(g.subject, subject) && g.status === 'granted' && g.scope.length === scope.length && g.scope.every((c) => scope.includes(c)));
  }
  private withdraw(key: string) {
    const now = Date.now();
    for (const [k, at] of this.withdrawn) if (now - at >= REQUEST_TTL_MS) this.withdrawn.delete(k);
    this.withdrawn.set(key, now);
  }

  private put(a: Approval) {
    this.approvals = [...this.approvals.filter((x) => x.key !== a.key), a];
  }
  private remove(key: string): Approval | undefined {
    const a = this.approvals.find((x) => x.key === key);
    if (a) this.approvals = this.approvals.filter((x) => x !== a);
    return a;
  }

  /** Takes the pairing link this tab holds, if any. */
  private async receive() {
    const params = takeInvite();
    if (params === null) return;
    const refuse = (text: string) => void (this.notice = { text, tone: 'alert' });
    if (params === 'expired') return refuse('This agent link has expired. Ask your agent for a new one.');
    const invite = parseInvite(params);
    if (!invite) return refuse('This agent link is incomplete. Copy the whole link from your agent.');
    if (invite.relay && !isAllowedRelay(invite.relay))
      return refuse('This agent link uses a relay this Changeling deployment does not allow, so this browser cannot reach the agent. Connect your agent through the default relay.');
    const connection = `${invite.peer}/${invite.route}`;
    const epoch = this.epoch;
    // A history's locator: the person approves the key it resolves to, or hears why there is none.
    const at = invite.locator ? await this.located(invite.locator) : { key: invite.subject! };
    if (epoch !== this.epoch) return;
    if ('why' in at) return refuse(`Your agent asked to connect to the history ${locatorText(invite.locator!)}, which Changeling can’t find: ${at.why}`);
    const subject = at.key;
    const [requested, harness] = await Promise.all([this.requested(invite.req, subject), routeHarness(invite.route)]);
    if (epoch !== this.epoch) return;
    // Its name, and its grant here (`presetOf`), once the pairings are read (a link from an agent already paired, `pair_again`).
    this.put({ kind: 'pair', key: `pair:${connection}`, invite, subject, connection, fingerprint: showFingerprint(invite.peer), name: null, harness, requested });
  }

  private async request(r: GrantRequest) {
    const key = `grant:${r.connection}\0${r.id}`;
    this.withdrawn.delete(key);
    const epoch = this.epoch;
    const c = this.connection(r.connection);
    // A pairing this browser no longer has can't be granted anything.
    if (!c) return;
    let subject: SubjectKey;
    if (r.locator !== undefined) {
      // A history's locator, resolved here (a read of GitHub, which tells the agent's computer nothing):
      // the person is asked for the key, and only Connect tells the agent what it is (`approve`).
      const l = parseLocator(r.locator)!;
      const at = await this.located(l);
      if (epoch !== this.epoch || this.withdrawn.has(key) || !this.offs.length) return;
      if ('why' in at) {
        // Nothing to approve: an entry of its own, which the person declines (or it expires on the agent's computer).
        this.unfound = [...this.unfound.filter((u) => u.key !== key), { key, connection: r.connection, request: r.id, name: c.name, locator: r.locator, why: at.why }];
        return;
      }
      subject = at.key;
    } else subject = r.subject;
    const requested = await this.requested(r.request, subject);
    // Stopped meanwhile (signed out, maybe into another account), settled, or no longer serving.
    if (epoch !== this.epoch || this.withdrawn.delete(key) || !this.offs.length) return;
    // Already granted just what it asks for here (the helper may not have heard yet): nothing to ask.
    const asks = requested ?? presetFor(kindOf(subject));
    if (this.covered(c.id, subject, asks)) return;
    // The fingerprint is the browser's own, from the endpoint it pinned, not the one the request carries.
    this.put({ kind: 'grant', key, request: r, asks, subject, connection: c.id, fingerprint: showFingerprint(c.peer), name: c.name, harness: c.harness, requested });
  }

  private gone(id: string, connection: string) {
    const key = `grant:${connection}\0${id}`;
    this.withdraw(key);
    const a = this.remove(key);
    this.unfound = this.unfound.filter((u) => u.key !== key);
    if (a) this.notice = { text: `${a.name ?? 'The agent'}’s request to read ${a.subject} is no longer waiting.`, tone: 'status', subject: a.subject };
  }

  /** Declines a request for a history that resolves to nothing (`unfound`), at the person's word. */
  async declineUnfound(u: Unfound): Promise<void> {
    this.withdraw(u.key);
    this.unfound = this.unfound.filter((x) => x.key !== u.key);
    if (this.connection(u.connection)) await relay.decline(u.connection, u.request);
    this.notice = { text: `Declined. ${u.name} can’t read the history ${u.locator} through this request.`, tone: 'status' };
  }

  /** Connects the agent of `a` with `scope`: pairs with a link's agent, or grants a paired agent's request. */
  async approve(a: Approval, scope: RelayCategory[]): Promise<void> {
    if (a.kind === 'pair') {
      // Its grant here, if it has one, isn't known before: Connect could widen it unseen.
      if (!this.pairingsRead) throw new Error('This browser’s agents haven’t been read yet. Try again.');
      // A link for a history's locator pairs with the key it resolved to.
      const { locator: _, ...invite } = a.invite;
      const c = await relay.pair({ ...invite, subject: a.subject, scope });
      this.channel?.postMessage('paired');
      this.remove(a.key);
      this.notice = { text: `Connected ${c.name} to ${a.subject}. If that isn’t what you asked your agent to connect to, revoke it.`, tone: 'status', subject: a.subject, persist: true, connection: c.id };
      this.connected = [...this.connected.filter((id) => id !== c.id), c.id];
    } else {
      // A request for a history's locator: the agent's computer hears the key it resolved to only now, the person having approved it.
      if (a.request.locator !== undefined) await relay.resolve(a.request.connection, a.request.id, a.subject);
      await relay.grant(a.request.connection, a.subject, scope);
      this.withdraw(a.key);
      this.remove(a.key);
      this.notice = { text: `${a.name ?? 'The agent'} can read ${a.subject}.`, tone: 'status', subject: a.subject };
    }
    await this.refresh();
  }

  /**
   * Declines `a`: tells a paired agent's computer, or drops a link's (whose
   * invitation then expires unused). A request of a pairing this browser no
   * longer has is only dropped here.
   */
  async decline(a: Approval): Promise<void> {
    if (a.kind === 'grant') {
      if (this.connection(a.request.connection)) await relay.decline(a.request.connection, a.request.id);
      this.withdraw(a.key);
    }
    this.remove(a.key);
    this.notice = { text: `Declined. ${a.name ?? 'The agent'} can’t read ${a.subject} through this request.`, tone: 'status', subject: a.subject };
  }

  /** Drops `a` here without telling the agent's computer (a decline that couldn't reach it); the request expires there. */
  dismiss(a: Approval): void {
    if (a.kind === 'grant') this.withdraw(a.key);
    this.remove(a.key);
    this.notice = { text: `Dismissed here. ${a.name ?? 'The agent'}’s request to read ${a.subject} expires on its computer.`, tone: 'status', subject: a.subject };
  }

  /** Grants `connection` `subject` with `scope`, or changes its scope; a narrower one drops what this tab kept of it. */
  async grant(connection: string, subject: SubjectKey, scope: RelayCategory[]): Promise<void> {
    const before = this.grants.find((g) => g.connection === connection && sameSubject(g.subject, subject));
    await relay.grant(connection, subject, scope);
    if (before && narrowed(before.scope, scope)) dropSnapshots(connection, subject);
    await this.refresh();
  }

  /** Revokes `connection`'s grant on `subject`. */
  async revokeGrant(connection: string, subject: SubjectKey): Promise<void> {
    await relay.revokeGrant(connection, subject);
    dropSnapshots(connection, subject);
    await this.refresh();
  }

  /** Disconnects a pairing; its grants stop here even when its computer can't be told (which rejects). */
  /** Ends `connection`'s Just connected mark: the person says it is the agent they asked for. */
  acknowledge(connection: string): void {
    this.connected = this.connected.filter((id) => id !== connection);
  }
  /** Dismisses the notice; a pairing's warning takes its Just connected mark with it. */
  dismissNotice(): void {
    if (this.notice?.connection) this.acknowledge(this.notice.connection);
    this.notice = null;
  }

  async unpair(connection: string): Promise<void> {
    try {
      await relay.revoke(connection);
    } finally {
      dropSnapshots(connection);
      this.acknowledge(connection);
      await this.refresh();
    }
  }

  /** Forgets a pairing on this browser only. */
  async forget(connection: string): Promise<void> {
    await relay.forget(connection);
    dropSnapshots(connection);
    this.acknowledge(connection);
    await this.refresh();
  }
}

export const relayAgents = new RelayAgents();
